/**
 * TranscriptTabView Component
 * ===========================
 * Tabbed interface for displaying meeting transcripts from multiple sources
 * (Consolidated, Fireflies, tl;dv)
 */

import React, { useState } from 'react';
import { parseTranscriptText, getAvailableSources, getPrimarySource } from '../utils/transcripts';

const TranscriptTabView = ({ transcriptData }) => {
  const [activeTab, setActiveTab] = useState(0);

  // Determine available sources
  const sourcesAvailable = getAvailableSources(transcriptData);
  const primarySource = getPrimarySource(transcriptData);

  // Build tab configuration
  const tabs = [
    {
      label: 'Consolidated',
      value: 'consolidated',
      data: {
        full_text: transcriptData?.full_text,
        summary: transcriptData?.summary,
        action_items: transcriptData?.action_items,
        key_points: transcriptData?.key_points
      },
      isPrimary: primarySource === 'consolidated' || !primarySource,
      sourceUrl: null
    }
  ];

  if (sourcesAvailable.includes('fireflies')) {
    tabs.push({
      label: 'Fireflies',
      value: 'fireflies',
      data: transcriptData?.fireflies,
      isPrimary: primarySource === 'fireflies',
      sourceUrl: transcriptData?.fireflies?.meeting_url
    });
  }

  if (sourcesAvailable.includes('tldv')) {
    tabs.push({
      label: 'tl;dv',
      value: 'tldv',
      data: transcriptData?.tldv,
      isPrimary: primarySource === 'tldv',
      sourceUrl: transcriptData?.tldv?.meeting_url
    });
  }

  if (sourcesAvailable.includes('manual')) {
    tabs.push({
      label: 'Manual',
      value: 'manual',
      data: transcriptData?.manual,
      isPrimary: primarySource === 'manual',
      sourceUrl: null
    });
  }

  if (sourcesAvailable.includes('recall')) {
    tabs.push({
      label: 'Recall',
      value: 'recall',
      data: transcriptData?.recall,
      isPrimary: primarySource === 'recall',
      sourceUrl: null
    });
  }

  const handleTabChange = (index) => {
    setActiveTab(index);
  };

  const renderTranscript = (data, sourceType) => {
    if (!data) {
      return (
        <p className="text-gray-500 text-center py-4">
          No transcript available
        </p>
      );
    }

    // Get transcript text - all sources use full_text
    const transcriptText = data.full_text;

    if (!transcriptText) {
      return (
        <p className="text-gray-500 text-center py-4">
          No transcript available
        </p>
      );
    }

    const parsedTranscript = parseTranscriptText(transcriptText);

    return (
      <div className="space-y-4">
        {/* Summary Section */}
        {data.summary && (
          <div className="bg-gray-100 rounded-lg p-4 mb-4">
            <h4 className="text-base font-semibold text-gray-900 mb-2">Summary</h4>
            <p className="text-gray-700 whitespace-pre-wrap">{data.summary}</p>
          </div>
        )}

        {/* Action Items */}
        {data.action_items && data.action_items.length > 0 && (
          <div className="mb-4">
            <h4 className="text-base font-semibold text-gray-900 mb-2">Action Items</h4>
            <ul className="list-disc list-inside space-y-1">
              {data.action_items.map((item, idx) => (
                <li key={idx} className="text-gray-700">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Key Points */}
        {data.key_points && data.key_points.length > 0 && (
          <div className="mb-4">
            <h4 className="text-base font-semibold text-gray-900 mb-2">Key Points</h4>
            <ul className="list-disc list-inside space-y-1">
              {data.key_points.map((point, idx) => (
                <li key={idx} className="text-gray-700">
                  {point}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Full Transcript */}
        <div>
          <h4 className="text-base font-semibold text-gray-900 mb-2">Transcript</h4>
          <div className="bg-gray-50 rounded-lg p-4 max-h-[600px] overflow-y-auto">
            {parsedTranscript.length > 0 ? (
              <div className="space-y-3">
                {parsedTranscript.map((item, index) => (
                  <div key={index} className="flex gap-3">
                    {item.timestamp && (
                      <span className="flex-shrink-0 text-sm text-gray-400 font-mono w-14">
                        {item.timestamp}
                      </span>
                    )}
                    <div className={item.timestamp ? '' : 'ml-0'}>
                      {item.speaker && (
                        <span className="font-medium text-primary">
                          {item.speaker}:
                        </span>
                      )}
                      <span className="text-gray-700 ml-2">{item.text}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No transcript content available
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // If only one source available, don't show tabs
  if (tabs.length === 1) {
    return (
      <div className="transcript-tab-view">
        {renderTranscript(tabs[0].data, tabs[0].value)}
      </div>
    );
  }

  return (
    <div className="transcript-tab-view">
      {/* Tab Headers */}
      <div className="border-b border-gray-200 mb-4">
        <div className="flex space-x-1">
          {tabs.map((tab, idx) => (
            <button
              key={idx}
              onClick={() => handleTabChange(idx)}
              className={`
                px-4 py-2 text-sm font-medium rounded-t-lg transition-colors
                ${activeTab === idx
                  ? 'bg-white border-b-2 border-primary text-primary'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }
              `}
            >
              <div className="flex items-center gap-2">
                <span>{tab.label}</span>
                {tab.isPrimary && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                    Primary
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {tabs.map((tab, idx) => (
        <div
          key={idx}
          role="tabpanel"
          hidden={activeTab !== idx}
          className={activeTab === idx ? 'block' : 'hidden'}
        >
          {activeTab === idx && (
            <>
              {tab.sourceUrl && (
                <div className="mb-4 text-sm">
                  <span className="text-gray-600">Source: </span>
                  <a
                    href={tab.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    View on {tab.label}
                  </a>
                </div>
              )}
              {renderTranscript(tab.data, tab.value)}
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default TranscriptTabView;
